import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {useQueryClient} from '@github-ui/react-query'
import {Link as RouterLink, useLocation, useNavigation} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {RepoIssuesSidebarLayoutEnabledContext} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {SavedViewsResponse, UpdateSavedViewResponse, UserShortcut} from '@github-ui/saved-views'
import {canShowCreateAction} from '@github-ui/saved-views/utils/can-show-create-action'
import {ViewsBackLink} from '@github-ui/saved-views/components/ViewsBackLink'
import {BUTTON_LABELS} from '@github-ui/saved-views/constants'
import {RepositorySavedViewContent} from '@github-ui/saved-views/components/detail/RepositorySavedViewContent'
import {ViewsIndexContent} from '@github-ui/saved-views/components/index/ViewsIndexContent'
import {RepositoryViewNewContent} from '@github-ui/saved-views/components/new/RepositoryViewNewContent'
import {useConfirmDiscardChanges} from '@github-ui/saved-views/hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '@github-ui/saved-views/hooks/use-saved-view-editor'
import {useNavigate} from '@github-ui/use-navigate'
import {UserContext} from '@github-ui/use-user'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {getIssuesRelayEnvironment} from '../components/issues-relay-environment'
import {isViewConfiguration} from '../components/board/issues/config-payload'
import {ConnectedSavedViewOptionsMenu} from '../components/saved-view-options/ConnectedSavedViewOptionsMenu'
import {
  SavedViewUrlConfiguration,
  viewConfigurationFromPayload,
} from '../components/saved-view-options/SavedViewUrlConfiguration'
import {
  useSavedViewConfiguration,
  useSavedViewConfigurationDirtyState,
} from '../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration} from '../contexts/views/view-configuration'
import {canonicalQueryKey, copyRouteQueryData} from '../helpers/saved-view-route-cache'
import {SAVED_VIEW_URL_PARAMS} from '../utils/saved-view-url-params'
import {issuesLayoutRoute, layoutCurrentUser} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {savedViewRoute, savedViewsRoute} from './saved-views-routes'
import {RepositorySavedViewBoard} from './RepositorySavedViewBoard'
import {savedViewContentPath, type SavedViewRoutePayload, savedViewRouteSearchParams} from './saved-view-route-payload'
import {TanStackSavedViewIssues} from './TanStackSavedViewIssues'
import {useSavedViewContentForQuery} from './use-saved-view-content-for-query'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue state:open',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

function useSavedViewsLayout(initialSavedViews?: SavedViewsResponse) {
  const {owner, repo} = useRouteParams(issuesLayoutRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  return {
    owner,
    repo,
    layoutPayload,
    renderSidebar: useRenderSidebar(owner, repo, layoutPayload, initialSavedViews),
  }
}

export function SavedViews() {
  const {data: savedViews} = useRouteQuery(savedViewsRoute, 'mainQuery')
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout(savedViews)
  const newViewUrl = `/${owner}/${repo}/issues/views/new`
  const teamPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const newViewAction = canShowCreateAction({savedViews, teamPersonalEnabled}) ? (
    <Button as={RouterLink} to={newViewUrl} variant="primary">
      {BUTTON_LABELS.newView}
    </Button>
  ) : undefined

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        title="Views"
        dialogTitle="Views"
        actions={newViewAction}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <UserContext value={layoutCurrentUser(layoutPayload)}>
          <ViewsIndexContent
            owner={owner}
            repo={repo}
            newViewUrl={newViewUrl}
            pinEnabled={layoutPayload?.pinnedViewsEnabled ?? false}
            teamPersonalEnabled={teamPersonalEnabled}
            initialData={savedViews}
          />
        </UserContext>
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedViewNew() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const navigate = useNavigate()
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty: editor.isDirty})
  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleBackClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    async event => {
      if (event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !editor.isDirty) return

      event.preventDefault()
      if (await confirmDiscardIfDirty()) navigate(viewsPath)
    },
    [confirmDiscardIfDirty, editor.isDirty, navigate, viewsPath],
  )

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        dialogTitle="Views"
        title="Create view"
        contextArea={<ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <RepositoryViewNewContent
          editor={editor}
          confirmDiscardIfDirty={confirmDiscardIfDirty}
          onBackLinkClick={handleBackClick}
          IssuesRenderer={TanStackSavedViewIssues}
        />
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedView() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const {data: route, dataUpdatedAt} = useSuspenseRouteQuery(savedViewRoute, 'mainQuery')
  const teamPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const currentUser = useMemo(() => layoutCurrentUser(layoutPayload), [layoutPayload])
  const basePath = `/${owner}/${repo}/issues/views`
  const applied = useMemo(() => appliedConfiguration(route), [route])

  const contentProps = {
    pinEnabled: layoutPayload?.pinnedViewsEnabled ?? false,
    perVisibilityPinCapEnabled: teamPersonalEnabled,
    renderSidebar,
    renderCreateIssueButton: () => <RepositoryCreateIssueButton owner={owner} repo={repo} />,
    initialData: route.view,
    initialDataUpdatedAt: route.view ? dataUpdatedAt : undefined,
    width: route.layout === 'board' ? ('full' as const) : undefined,
  }

  // The server says whether this repository offers the board, and that gates the whole
  // view-options experience. Off, none of it mounts.
  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <UserContext value={currentUser}>
        {route.boardEnabled ? (
          <SavedViewUrlConfiguration applied={applied} baseline={route.configuration} basePath={basePath}>
            <ConfiguredSavedViewContent
              {...contentProps}
              basePath={basePath}
              saveConfiguration={route.configuration !== undefined}
            />
          </SavedViewUrlConfiguration>
        ) : (
          <RepositorySavedViewContent IssuesRenderer={TanStackSavedViewIssues} {...contentProps} />
        )}
      </UserContext>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

/**
 * The configuration the page is drawn with: the served layout and board axes over the stored
 * settings. Read from the response rather than the URL, because the server can ignore or fall back
 * on what the URL asks for.
 */
function appliedConfiguration(route: SavedViewRoutePayload): ViewConfiguration {
  return viewConfigurationFromPayload(
    {
      layout: route.layout,
      groupBy: route.layout === 'board' ? route.content.columnField : (route.configuration?.groupBy ?? null),
      swimlaneBy: route.layout === 'board' ? route.content.rowField : (route.configuration?.swimlaneBy ?? null),
    },
    route.configuration,
  )
}

function ConfiguredSavedViewContent({
  basePath,
  saveConfiguration,
  ...props
}: Omit<
  React.ComponentProps<typeof RepositorySavedViewContent<ViewConfiguration>>,
  'IssuesRenderer' | 'filterBarActions' | 'configuration' | 'isConfigurationDirty' | 'onSaveSuccess' | 'isNavigating'
> & {
  basePath: string
  /** Whether Save sends the configuration. Off when the server sent no stored one to compare against. */
  saveConfiguration: boolean
}) {
  const configuration = useSavedViewConfiguration()
  const {isConfigurationDirty} = useSavedViewConfigurationDirtyState()
  const onSaveSuccess = useClearSavedOverrides()
  const isNavigating = useNavigation().state !== 'idle'

  return (
    <RepositorySavedViewContent<ViewConfiguration>
      {...props}
      IssuesRenderer={SavedViewIssuesForLayout}
      filterBarActions={({isSaving}) => <ConnectedSavedViewOptionsMenu basePath={basePath} disabled={isSaving} />}
      configuration={saveConfiguration ? configuration : undefined}
      isConfigurationDirty={isConfigurationDirty}
      onSaveSuccess={onSaveSuccess}
      isNavigating={isNavigating}
    />
  )
}

/**
 * After Save, the saved query and configuration are the baseline, so their URL overrides go. The
 * route response moves to the clean URL first, so the board on screen is kept with its loaded pages.
 */
function useClearSavedOverrides() {
  const navigate = useNavigate()
  const {pathname, search} = useLocation()
  const queryClient = useQueryClient()
  const mainQueryKey = useRouteQueryKey(savedViewRoute, 'mainQuery')

  return useCallback(
    (response: UpdateSavedViewResponse<ViewConfiguration>) => {
      const savedConfiguration = isViewConfiguration(response.configuration) ? response.configuration : undefined
      const destination = new URLSearchParams(search)
      destination.delete('q')
      destination.delete('page')
      if (savedConfiguration) {
        for (const param of SAVED_VIEW_URL_PARAMS) destination.delete(param)
      }
      if (mainQueryKey) {
        copyRouteQueryData(
          queryClient,
          mainQueryKey,
          canonicalQueryKey(mainQueryKey, savedViewContentPath(pathname), savedViewRouteSearchParams(destination)),
          savedConfiguration,
          response.savedView,
        )
      }
      const query = destination.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [mainQueryKey, navigate, pathname, queryClient, search],
  )
}

function RepositoryCreateIssueButton({owner, repo}: {owner: string; repo: string}) {
  const environment = getIssuesRelayEnvironment()
  const navigate = useNavigate()

  return (
    <RelayEnvironmentProvider environment={environment}>
      <CreateIssueButton
        label="New issue"
        navigate={navigate}
        optionConfig={{
          issueCreateArguments: {repository: {owner, name: repo}},
          showRepositoryPicker: false,
          showFullScreenButton: true,
          canBypassTemplateSelection: true,
          navigate,
        }}
      />
    </RelayEnvironmentProvider>
  )
}

/** Renders the board or the list, whichever layout the server served. */
function SavedViewIssuesForLayout({
  owner,
  repo,
  query,
  preheatEnabled,
}: {
  owner: string
  repo: string
  query: string
  preheatEnabled?: boolean
}) {
  const {id} = useRouteParams(savedViewRoute)
  const {route, dataUpdatedAt, isFetching, isError} = useSavedViewContentForQuery(query)
  const navigation = useNavigation()

  return route.layout === 'board' && !isError ? (
    <RepositorySavedViewBoard
      board={route.content}
      owner={owner}
      repo={repo}
      id={id}
      query={query}
      dataUpdatedAt={route.boardDataUpdatedAt ?? dataUpdatedAt}
      isNavigating={navigation.state !== 'idle' || isFetching}
      basePath={`/${owner}/${repo}/issues/views`}
    />
  ) : (
    <TanStackSavedViewIssues owner={owner} repo={repo} query={query} preheatEnabled={preheatEnabled} />
  )
}
